import React, { useState } from 'react';

const INITIAL_TASKS = [
  { id: 1, title: 'Review Q1 budget report', priority: 'High', status: 'Pending', assignee: 'Alice', due: '2026-04-05' },
  { id: 2, title: 'Update vendor contracts', priority: 'Medium', status: 'In Progress', assignee: 'Bob', due: '2026-04-10' },
  { id: 3, title: 'Deploy hotfix to production', priority: 'High', status: 'Pending', assignee: 'Carol', due: '2026-04-02' },
  { id: 4, title: 'Prepare onboarding docs', priority: 'Low', status: 'Done', assignee: 'Dave', due: '2026-03-30' },
  { id: 5, title: 'Conduct security audit', priority: 'High', status: 'In Progress', assignee: 'Alice', due: '2026-04-15' },
  { id: 6, title: 'Client demo preparation', priority: 'Medium', status: 'Pending', assignee: 'Bob', due: '2026-04-08' },
];

const PRIORITIES = ['All', 'High', 'Medium', 'Low'];
const STATUSES = ['All', 'Pending', 'In Progress', 'Done'];
const ASSIGNEES = ['Alice', 'Bob', 'Carol', 'Dave', 'Eve'];

export default function TaskManager() {
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('due');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'Medium', status: 'Pending', assignee: 'Alice', due: '' });
  const [newTaskErrors, setNewTaskErrors] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = tasks
    .filter(t => filterPriority === 'All' || t.priority === filterPriority)
    .filter(t => filterStatus === 'All' || t.status === filterStatus)
    .filter(t => t.title.toLowerCase().includes(search.toLowerCase()) || t.assignee.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'due') return a.due.localeCompare(b.due);
      if (sortBy === 'priority') return ['High','Medium','Low'].indexOf(a.priority) - ['High','Medium','Low'].indexOf(b.priority);
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      return 0;
    });

  const startEdit = (task) => { setEditingId(task.id); setEditForm({ ...task }); };
  const saveEdit = () => {
    setTasks(prev => prev.map(t => t.id === editingId ? { ...editForm } : t));
    setEditingId(null);
  };

  const validateNew = () => {
    const errors = {};
    if (!newTask.title.trim()) errors.title = 'Title required';
    if (!newTask.due) errors.due = 'Due date required';
    return errors;
  };

  const addTask = () => {
    const errors = validateNew();
    if (Object.keys(errors).length) { setNewTaskErrors(errors); return; }
    setTasks(prev => [...prev, { ...newTask, id: Date.now() }]);
    setNewTask({ title: '', priority: 'Medium', status: 'Pending', assignee: 'Alice', due: '' });
    setNewTaskErrors({});
    setShowAddModal(false);
  };

  const deleteTask = (id) => { setTasks(prev => prev.filter(t => t.id !== id)); setDeleteConfirmId(null); };

  const priorityColor = (p) => ({ High: '#c62828', Medium: '#e65100', Low: '#1b5e20' }[p] || '#333');
  const statusColor = (s) => ({ Pending: '#1565c0', 'In Progress': '#6a1b9a', Done: '#2e7d32' }[s] || '#333');

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">📋 Task Manager <span className="eval-badge-scenario">Multi-action / Filter / CRUD</span></h2>
      <p className="eval-desc">Test: filtering, sorting, inline editing, modal add, delete confirmation, search across tasks.</p>

      <div className="tm-toolbar">
        <input className="tm-search" placeholder="Search tasks or assignee..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="tm-filters">
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="due">Sort: Due Date</option>
            <option value="priority">Sort: Priority</option>
            <option value="title">Sort: Title</option>
          </select>
        </div>
        <button className="eval-btn eval-btn-primary" onClick={() => setShowAddModal(true)}>+ Add Task</button>
      </div>

      <table className="eval-table">
        <thead>
          <tr><th>Title</th><th>Priority</th><th>Status</th><th>Assignee</th><th>Due Date</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan="6" style={{textAlign:'center',color:'#aaa',padding:'24px'}}>No tasks match filters.</td></tr>}
          {filtered.map(task => editingId === task.id ? (
            <tr key={task.id} className="tm-editing-row">
              <td><input value={editForm.title} onChange={e => setEditForm(f=>({...f,title:e.target.value}))} className="tm-inline-input" /></td>
              <td>
                <select value={editForm.priority} onChange={e => setEditForm(f=>({...f,priority:e.target.value}))} className="tm-inline-select">
                  {['High','Medium','Low'].map(p=><option key={p}>{p}</option>)}
                </select>
              </td>
              <td>
                <select value={editForm.status} onChange={e => setEditForm(f=>({...f,status:e.target.value}))} className="tm-inline-select">
                  {['Pending','In Progress','Done'].map(s=><option key={s}>{s}</option>)}
                </select>
              </td>
              <td>
                <select value={editForm.assignee} onChange={e => setEditForm(f=>({...f,assignee:e.target.value}))} className="tm-inline-select">
                  {ASSIGNEES.map(a=><option key={a}>{a}</option>)}
                </select>
              </td>
              <td><input type="date" value={editForm.due} onChange={e => setEditForm(f=>({...f,due:e.target.value}))} className="tm-inline-input" /></td>
              <td>
                <button className="eval-btn eval-btn-sm eval-btn-success" onClick={saveEdit}>Save</button>
                <button className="eval-btn eval-btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={task.id}>
              <td>{task.title}</td>
              <td><span className="tm-chip" style={{color: priorityColor(task.priority), borderColor: priorityColor(task.priority)}}>{task.priority}</span></td>
              <td><span className="tm-chip" style={{color: statusColor(task.status), borderColor: statusColor(task.status)}}>{task.status}</span></td>
              <td>{task.assignee}</td>
              <td>{task.due}</td>
              <td>
                <button className="eval-btn eval-btn-sm" onClick={() => startEdit(task)}>Edit</button>
                <button className="eval-btn eval-btn-sm eval-btn-danger" onClick={() => setDeleteConfirmId(task.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Modal */}
      {showAddModal && (
        <div className="eval-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="eval-modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Task</h3>
            <div className="eval-modal-field">
              <label>Title *</label>
              <input value={newTask.title} onChange={e => setNewTask(t=>({...t,title:e.target.value}))} className={newTaskErrors.title ? 'input-err' : ''} placeholder="Task title" />
              {newTaskErrors.title && <span className="eval-err">{newTaskErrors.title}</span>}
            </div>
            <div className="eval-modal-field">
              <label>Priority</label>
              <select value={newTask.priority} onChange={e => setNewTask(t=>({...t,priority:e.target.value}))}>
                {['High','Medium','Low'].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="eval-modal-field">
              <label>Status</label>
              <select value={newTask.status} onChange={e => setNewTask(t=>({...t,status:e.target.value}))}>
                {['Pending','In Progress','Done'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="eval-modal-field">
              <label>Assignee</label>
              <select value={newTask.assignee} onChange={e => setNewTask(t=>({...t,assignee:e.target.value}))}>
                {ASSIGNEES.map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
            <div className="eval-modal-field">
              <label>Due Date *</label>
              <input type="date" value={newTask.due} onChange={e => setNewTask(t=>({...t,due:e.target.value}))} className={newTaskErrors.due ? 'input-err' : ''} />
              {newTaskErrors.due && <span className="eval-err">{newTaskErrors.due}</span>}
            </div>
            <div className="eval-modal-actions">
              <button className="eval-btn eval-btn-primary" onClick={addTask}>Add Task</button>
              <button className="eval-btn" onClick={() => { setShowAddModal(false); setNewTaskErrors({}); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="eval-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="eval-modal eval-modal-sm" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this task? This action cannot be undone.</p>
            <div className="eval-modal-actions">
              <button className="eval-btn eval-btn-danger" onClick={() => deleteTask(deleteConfirmId)}>Yes, Delete</button>
              <button className="eval-btn" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
